import './index.css';
import { jsx, jsxs, Fragment as Fragment$1 } from 'react/jsx-runtime';
import { useMemo, Fragment, useState, useRef } from 'react';
import classnames from 'classnames';
import { isSameMonth, isSameDay, isToday, format, isFirstDayOfMonth, isLastDayOfMonth } from 'date-fns';
import { daysInWeek } from 'date-fns/constants';
import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { Action } from '@hh.ru/magritte-ui-action';
import { BottomSheet } from '@hh.ru/magritte-ui-bottom-sheet';
import { useDisabledDatesFunc } from './useDisabledDatesFunc.js';
import { useIntervalPicker } from './useIntervalPicker.js';
import { useDayPickerDates, flatten, getInfoByDateType } from './utils.js';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { NavigationBar } from '@hh.ru/magritte-ui-navigation-bar';
import { VSpacing } from '@hh.ru/magritte-ui-spacing';
import { TooltipHover } from '@hh.ru/magritte-ui-tooltip';
import { Text } from '@hh.ru/magritte-ui-typography';
import { s as styles } from './date-picker-CgUVI9LP.js';
import './AsyncDataContext.js';

const DayPicker = ({ disabledDates, minDate, maxDate, displayDate, selectedDate, showAdjacentDays, onDateSelect, }) => {
    const dates = useDayPickerDates(displayDate);
    const disabledDatesFunc = useDisabledDatesFunc(dates[0], dates[dates.length - 1], disabledDates, minDate, maxDate);
    const forceDisabled = useDisabled();
    const onClickHandlers = useMemo(() => {
        return dates.map((date) => {
            return () => onDateSelect?.(date);
        });
    }, [dates, onDateSelect]);
    return (jsx("div", { className: classnames(styles.calendar, styles.calendarDays), "data-qa": "date-picker-days", children: dates.map((date, index) => {
            const isAdjacent = !isSameMonth(date, displayDate);
            const isDisabled = isAdjacent || forceDisabled || disabledDatesFunc(date);
            const isSelected = !!selectedDate && isSameDay(date, selectedDate);
            const display = !isAdjacent || showAdjacentDays;
            return (jsx("div", { className: styles.item, children: display && (jsx("button", { className: classnames(styles.text, {
                        [styles.textDisabled]: isDisabled,
                        [styles.textSelected]: isSelected,
                        [styles.textToday]: isToday(date),
                        [styles.textUnselected]: !isDisabled && !isSelected,
                    }), disabled: isDisabled || isSelected, onClick: onClickHandlers[index], "data-date": format(date, 'yyyy-MM-dd'), "data-qa": classnames('date-picker-day', {
                        'date-picker-day-disabled': isDisabled,
                        'date-picker-day-selected': isSelected,
                        'date-picker-day-today': isToday(date),
                    }), children: format(date, 'dd') })) }, `${date}`));
        }) }));
};
const IntervalDayPicker = ({ disabledDates, minDate, maxDate, displayDate, selectionStart, selectionEnd, hoveredDate, dragState, onStartSelect, onEndSelect, onDateHover, onDateDrag, showAdjacentDays, }) => {
    const dates = useDayPickerDates(displayDate);
    const disabledDatesFunc = useDisabledDatesFunc(dates[0], dates[dates.length - 1], disabledDates, minDate, maxDate);
    const datesProps = useIntervalPicker({
        dates,
        disabledDatesFunc,
        selectionStart,
        selectionEnd,
        hoveredDate,
        dragState,
        onStartSelect,
        onEndSelect,
        onDateHover,
        onDateDrag,
        isEqual: isSameDay,
    });
    return (jsx("div", { className: classnames(styles.calendar, styles.calendarDays), "data-qa": "date-picker-days", children: dates.map((date, index) => {
            const isAdjacent = !isSameMonth(date, displayDate);
            const { isDisabled: _isDisabled, isIntervalStart, isIntervalEnd, isSelected, isDraggable, isDragSource, isDropTarget, isWithinInterval, mouseHandlers, dragSourceHandlers, dropTargetHandlers, } = datesProps[index];
            const isDisabled = isAdjacent || _isDisabled;
            const isIntervalRowStart = isWithinInterval &&
                !isIntervalStart &&
                (index % daysInWeek === 0 || (!showAdjacentDays && isFirstDayOfMonth(date)));
            const isIntervalRowEnd = isWithinInterval &&
                !isIntervalEnd &&
                (index % daysInWeek === daysInWeek - 1 || (!showAdjacentDays && isLastDayOfMonth(date)));
            const display = !isAdjacent || showAdjacentDays;
            return (jsx("div", { className: classnames(styles.item, {
                    [styles.itemInterval]: isWithinInterval,
                    [styles.itemIntervalStart]: isIntervalStart,
                    [styles.itemIntervalEnd]: isIntervalEnd,
                    [styles.itemIntervalRowStart]: isIntervalRowStart,
                    [styles.itemIntervalRowEnd]: isIntervalRowEnd,
                }), children: display && (jsxs(Fragment, { children: [jsx("button", { ...mouseHandlers, ...(!isDisabled ? dragSourceHandlers : {}), ...(!isDisabled ? dropTargetHandlers : {}), className: classnames(styles.text, {
                                [styles.textDisabled]: isDisabled,
                                [styles.textSelected]: isSelected,
                                [styles.textDraggable]: !isDisabled && isDraggable,
                                [styles.textDragSource]: isDragSource,
                                [styles.textDropTarget]: isDropTarget,
                                [styles.textToday]: isToday(date),
                                [styles.textUnselected]: !isDisabled && !isSelected && !isDragSource && !isDropTarget,
                            }), disabled: isDisabled || isSelected || isDragSource, draggable: !isDisabled && isDraggable, "data-date": format(date, 'yyyy-MM-dd'), "data-qa": classnames('date-picker-day', {
                                'date-picker-day-disabled': isDisabled,
                                'date-picker-day-selected': isSelected,
                                'date-picker-day-today': isToday(date),
                            }), children: format(date, 'dd') }), isWithinInterval && jsx("div", { className: styles.substrate })] })) }, `${date}`));
        }) }));
};
const InfoDayTooltipWrapper = ({ children, tooltip }) => {
    const activatorRef = useRef(null);
    return (jsxs(Fragment$1, { children: [jsx("div", { className: styles.item, ref: activatorRef, children: children }), tooltip && (jsx(TooltipHover, { placement: "top-center", activatorRef: activatorRef, children: tooltip }))] }));
};
const InfoDayPicker = ({ displayDate, holidays: _holidays, shortWorkdays: _shortWorkdays, }) => {
    const dates = useDayPickerDates(displayDate);
    const [bottomSheetState, setBottomSheetState] = useState({ show: false, text: '', header: '' });
    const onCloseBottomSheet = () => setBottomSheetState({ show: false, text: '', header: '' });
    const holidays = useMemo(() => _holidays && flatten(_holidays), [_holidays]);
    const shortWorkdays = useMemo(() => _shortWorkdays && flatten(_shortWorkdays), [_shortWorkdays]);
    return (jsxs("div", { className: classnames(styles.calendar, styles.calendarDays), "data-qa": "date-picker-days", children: [dates.map((date) => {
                const isAdjacent = !isSameMonth(date, displayDate);
                const holiday = holidays?.find((holiday) => isSameDay(date, holiday.date));
                const shortWorkday = shortWorkdays?.find((shortWorkday) => isSameDay(date, shortWorkday.date));
                const isWorkDay = !holiday && !shortWorkday;
                const info = getInfoByDateType(holiday, shortWorkday);
                const display = !isAdjacent;
                return (jsx(InfoDayTooltipWrapper, { tooltip: info?.text, children: display && (jsxs(Fragment, { children: [jsx("button", { className: classnames(styles.text, {
                                    [styles.textHoliday]: holiday,
                                    [styles.textShortWorkday]: shortWorkday,
                                    [styles.textWorkday]: isWorkDay,
                                }), onClick: () => {
                                    if (info) {
                                        setBottomSheetState({ show: true, ...info });
                                    }
                                }, disabled: !info, "data-date": format(date, 'yyyy-MM-dd'), "data-qa": classnames('date-picker-day', {
                                    'date-picker-day-workday': isWorkDay,
                                    'date-picker-day-short-workday': shortWorkday,
                                    'date-picker-day-holyday': holiday,
                                    'date-picker-day-today': isToday(date),
                                }), children: format(date, 'dd') }), jsx("div", { className: styles.substrate })] })) }, `${date}`));
            }), jsxs(BottomSheet, { visible: bottomSheetState.show, header: jsx(NavigationBar, { title: bottomSheetState.header, right: jsx(Action, { icon: CrossOutlinedSize24, onClick: onCloseBottomSheet }), showDivider: "always" }), onClose: onCloseBottomSheet, children: [jsx(Text, { Element: "span", typography: "label-2-regular", children: bottomSheetState.text }), jsx(VSpacing, { default: 48 })] })] }));
};

export { DayPicker, InfoDayPicker, IntervalDayPicker };
//# sourceMappingURL=DayPicker.js.map
